import { QuantitySelectorComponent } from '@theme/component-quantity-selector';

/**
 * A custom element that allows the user to select a quantity in the cart.
 * Extends QuantitySelectorComponent but uses absolute max limits instead of effective max.
 * Semantics: "What should the total quantity BE in the cart" vs "How many to ADD to cart"
 *
 * @extends {QuantitySelectorComponent}
 */
export class CartQuantitySelectorComponent extends QuantitySelectorComponent {
  /** @type {string | null} Stillwell: the number in the field when it got focus (see setQuantity) */
  #valueAtFocus = null;
  /** @type {string | null} Stillwell: while setQuantity runs, the value that needs no cart update */
  #unchangedValue = null;

  /**
   * Stillwell: Enter (the phone keyboard's Go/Done key) confirms the typed quantity, the same as leaving the field.
   * The field sits in the cart form, whose first submit button is Check out, so Enter used to send the shopper
   * straight to checkout. On touch screens the field is left (the keyboard closes); with a mouse and keyboard the
   * focus comes back to the field.
   * @param {KeyboardEvent} event
   */
  confirmOnEnter(event) {
    const { quantityInput } = this.refs;
    if (event.key !== 'Enter' || event.isComposing || event.target !== quantityInput) return;
    event.preventDefault();
    quantityInput.blur();
    if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) quantityInput.focus({ preventScroll: true });
  }

  /**
   * @param {FocusEvent} event
   */
  selectInputValue(event) {
    super.selectInputValue(event);
    if (document.activeElement === this.refs.quantityInput) this.#valueAtFocus = this.refs.quantityInput.value;
  }

  /**
   * Stillwell: leaving the field with the number it had when it got focus sends nothing (a tap on the number and a
   * tap away used to re-send the same quantity: a cart update and a full re-render with the prices shimmering).
   * @param {Event} event
   */
  setQuantity(event) {
    this.#unchangedValue = this.#valueAtFocus;
    this.#valueAtFocus = null;
    try {
      super.setQuantity(event);
    } finally {
      this.#unchangedValue = null;
    }
  }

  onQuantityChange() {
    if (this.#unchangedValue !== null && this.refs.quantityInput.value === this.#unchangedValue) return;
    super.onQuantityChange();
  }

  /**
   * Gets the effective maximum value for cart quantity selector
   * Cart page: uses absolute max (how much can be in cart total)
   * @returns {number | null} The effective max, or null if no max
   */
  getEffectiveMax() {
    const { max } = this.getCurrentValues();
    return max; // Cart uses absolute max, not max minus cart quantity
  }

  /**
   * Updates button states based on current value and limits
   * Cart buttons are always managed client-side, never server-disabled
   */
  updateButtonStates() {
    const { minusButton, plusButton } = this.refs;
    const { min, value } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();

    // Cart buttons are always dynamically managed
    const minusOff = value <= min;
    const plusOff = effectiveMax !== null && value >= effectiveMax;
    // Stillwell: the focused button is about to be disabled (− down to the minimum, + up to the maximum): the focus
    // goes to the quantity field for keyboard users (to the other button after a tap: no phone keyboard popping up)
    // instead of falling to the page
    const active = document.activeElement;
    if ((minusOff && active === minusButton) || (plusOff && active === plusButton)) {
      const other = active === minusButton ? plusButton : minusButton;
      const otherOff = active === minusButton ? plusOff : minusOff;
      const next = active.matches(':focus-visible') || otherOff ? this.refs.quantityInput : other;
      next?.focus({ preventScroll: true });
    }
    minusButton.disabled = minusOff;
    plusButton.disabled = plusOff;
  }
}

if (!customElements.get('cart-quantity-selector-component')) {
  customElements.define('cart-quantity-selector-component', CartQuantitySelectorComponent);
}
